// Colour maths for company branding. A company can pick any colour, so everything derived from
// it is checked for contrast (WCAG 2.x) rather than assumed to work on white or on dark.

export type RGB = [number, number, number];

export const WHITE: RGB = [255, 255, 255];
export const BLACK: RGB = [0, 0, 0];
/** Bootstrap 5.3's dark-mode body background. */
export const DARK_BG: RGB = [33, 37, 41];

export const hexToRgb = (hex?: string | null): RGB | null => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec((hex || "").trim());
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
};

export const rgbToHex = ([r, g, b]: RGB) =>
  "#" + [r, g, b].map((x) => Math.round(Math.max(0, Math.min(255, x))).toString(16).padStart(2, "0")).join("");

export const rgbList = ([r, g, b]: RGB) => `${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}`;

/** `amount` 0..1 of the way from `a` to `b`. */
export const mix = (a: RGB, b: RGB, amount: number): RGB =>
  [0, 1, 2].map((i) => a[i] + (b[i] - a[i]) * amount) as RGB;

const channel = (c: number) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

export const luminance = ([r, g, b]: RGB) => 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);

export const contrast = (a: RGB, b: RGB) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/** Black or white, whichever reads better on `bg`. */
export const textOn = (bg: RGB): RGB => (contrast(bg, WHITE) >= contrast(bg, BLACK) ? WHITE : BLACK);

/**
 * `color` moved toward `toward` in small steps until it reaches `ratio` against `bg`. Used for
 * link and outline text, so a pale brand colour still gives readable text on white, and a dark
 * one stays readable on the dark-mode background.
 */
export const readableOn = (color: RGB, bg: RGB, toward: RGB, ratio = 4.5): RGB => {
  for (let step = 0; step <= 20; step++) {
    const c = mix(color, toward, step * 0.05);
    if (contrast(c, bg) >= ratio) return c;
  }
  return toward;
};
